import { ActiveDevice, Room, RoomNameService, Stream } from "@domain";
import {
  AsyncInteractor,
  IBlocAsyncInteractor
} from "@kernel/frontend-react-app";
import { CreateLocalParticipantInteractor } from "./CreateLocalParticipantInteractor";
import { StartMediaStreamInteractor } from "../../local";
import { CreateRoomInteractor } from "./CreateRoomInteractor";
import { UserType } from "../UserType";

export interface ChangeRoomInteractorProps {
  room: Room;
  videoElement: HTMLVideoElement;
  type: UserType;
  activeDevice?: ActiveDevice;
  displayName?: string;
  isScreenSharing: boolean;
}

export class ChangeRoomInteractor
  extends AsyncInteractor<null, ChangeRoomInteractorProps, Room>
  implements IBlocAsyncInteractor<Room, ChangeRoomInteractorProps>
{
  private createLocalParticipant: CreateLocalParticipantInteractor;
  private createRoom: CreateRoomInteractor;
  private startMediaStream: StartMediaStreamInteractor;

  protected constructor() {
    super(null);
    this.createLocalParticipant = CreateLocalParticipantInteractor.create();
    this.startMediaStream = StartMediaStreamInteractor.create();
    this.createRoom = CreateRoomInteractor.create();
  }
  public async execute({
    room,
    videoElement,
    activeDevice,
    displayName,
    type,
    isScreenSharing = false
  }: ChangeRoomInteractorProps): Promise<Room> {
    const stream = await this.createLocalStream(
      videoElement,
      activeDevice,
      isScreenSharing
    );
    if (this.hasLocalParticipantStream(room)) {
      const r = this.changeLocalStream(
        room,
        stream,
        videoElement,
        activeDevice
      );
      return await this.changeRemoteStream(r, stream);
    }
    const newRoom = await this.createRoomWithLocalParticipant(
      room.name,
      stream,
      type,
      activeDevice,
      displayName
    );
    return newRoom;
  }

  private hasLocalParticipant(room: Room): boolean {
    return room.localParticipant !== null;
  }

  private hasLocalParticipantStream(room: Room): boolean {
    if (!this.hasLocalParticipant(room)) return false;
    return room.localParticipant!.stream !== null;
  }

  private async createRoomWithLocalParticipant(
    roomName: string,
    stream: Stream,
    type: UserType,
    activeDevice?: ActiveDevice,
    displayName?: string
  ): Promise<Room> {
    if (!activeDevice) {
      throw new Error("SelectedDevice is not set");
    }
    const participant = await this.createLocalParticipant.execute({
      displayName:
        displayName ??
        RoomNameService.generateRandomName(["_ADJECTIVE__NOUN_"]),
      type,
      activeDevice,
      stream
    });
    return this.createRoom.execute({
      roomName,
      localParticipant: participant
    });
  }

  private async createLocalStream(
    videoElement: HTMLVideoElement,
    activeDevice?: ActiveDevice,
    isScreenSharing: boolean = false
  ): Promise<Stream> {
    if (!activeDevice) {
      throw new Error("SelectedDevice is not set");
    }
    if (isScreenSharing) {
      return Stream.create({
        mediaStream: new MediaStream(),
        local: true,
        sourceType: "screen"
      });
    }
    return await this.startMediaStream.execute({
      selectedDevice: activeDevice,
      videoElement: videoElement,
      isScreenSource: isScreenSharing
    });
  }

  private changeLocalStream(
    room: Room,
    stream: Stream,
    videoElement: HTMLVideoElement,
    activeDevice?: ActiveDevice
  ): Room {
    if (!activeDevice) {
      throw new Error("SelectedDevice is not set");
    }
    room.localParticipant!.stream = stream;
    room.localParticipant!.stream!.start(videoElement);
    return Room.createFromRoom(room);
  }

  private async changeRemoteStream(room: Room, stream: Stream): Promise<Room> {
    const participants = room.getRemoteParticipants();
    for (const participant of participants.getItems()) {
      await participant.peer.replaceTrack(stream.mediaStream);
    }
    return room;
  }

  public static create() {
    return new ChangeRoomInteractor();
  }
}
